import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable25 = ({ data }) => {
  const pdfRef = useRef();

  // Numeric conversion & cleaning
  const dataset = data.map((d) => ({
    ...d,
    total: Number(d.all_races),
    white: Number(d.white_alone),
    percent: Number(d.percentage),
  }));

  // Computed Values
  const years = dataset.map((d) => d.year);

  const avgEmployment = (
    dataset.reduce((s, v) => s + v.total, 0) / dataset.length
  ).toFixed(2);

  const avgPercentage = (
    dataset.reduce((s, v) => s + v.percent, 0) / dataset.length
  ).toFixed(2);

  const maxYear = dataset.reduce((a, b) => (a.total > b.total ? a : b));
  const minYear = dataset.reduce((a, b) => (a.total < b.total ? a : b));

  const trendDirection =
    dataset[dataset.length - 1].total > dataset[0].total
      ? "Increasing 📈"
      : "Decreasing 📉";

  // Chart Data
  const chartData = {
    labels: years,
    datasets: [
      {
        type: "bar",
        label: "Total Employment (All Races)",
        data: dataset.map((i) => i.total),
        backgroundColor: "#FB2C3660",
        borderColor: "#FB2C36",
        borderWidth: 2,
        yAxisID: "y1",
      },
      {
        type: "line",
        label: "White Alone Percentage",
        data: dataset.map((i) => i.percent),
        borderColor: "#9CA3AF",
        backgroundColor: "#9CA3AF50",
        borderWidth: 3,
        tension: 0.4,
        yAxisID: "y2",
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment & White Alone Share — Yearly Comparison",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y1: {
        title: { display: true, text: "Total Employment" },
        position: "left",
      },
      y2: {
        title: { display: true, text: "White Alone Percentage (%)" },
        position: "right",
        grid: { drawOnChartArea: false },
      },
      x: { title: { display: true, text: "Year" } },
    },
  };

  // PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text("20250916.v3.198", 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("EngTable25.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white shadow-xl rounded-xl">
        <h2 className="text-2xl text-center font-bold text-gray-900 mb-6">
          Employment & White Alone Representation by Year
        </h2>

        {/* Insights Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="p-4 bg-blue-50 shadow rounded-lg">
            <p className="text-lg font-semibold text-blue-900">
              📈 Highest Employment
            </p>
            <p className="text-gray-700">
              {maxYear.year}: {maxYear.total.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-red-50 shadow rounded-lg">
            <p className="text-lg font-semibold text-red-800">
              📉 Lowest Employment
            </p>
            <p className="text-gray-700">
              {minYear.year}: {minYear.total.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-50 shadow rounded-lg">
            <p className="text-lg font-semibold text-green-800">
              📊 Average Employment
            </p>
            <p className="text-gray-700">
              {avgEmployment.toLocaleString("en-US")}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Data Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full text-center shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    <th className="p-2">Total Employment</th>
                    <th className="p-2">White Alone</th>
                    <th className="p-2">% Share</th>
                  </tr>
                </thead>
                <tbody>
                  {dataset.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-100">
                      <td className="p-2 font-semibold">{row.year}</td>
                      <td className="p-2">
                        {row.total.toLocaleString("en-US")}
                      </td>
                      <td className="p-2">
                        {row.white.toLocaleString("en-US")}
                      </td>
                      <td className="p-2 font-medium">{row.percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable25;
